/* Состояние входа для React. Токен параллельно кладётся в data/api.ts —
   слой данных не должен знать про OIDC, а слой auth не должен знать про запросы. */
import { useEffect, useState } from 'react'
import { setAuthToken, setUnauthorizedHandler } from '@/data/api'
import { restoreSession, signIn, toSession, userManager, type Session } from './oidc'

export type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous'; error: string | null }
  | { status: 'authenticated'; session: Session }

export function useSession(): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false

    const apply = (session: Session | null, error: string | null = null) => {
      if (cancelled) return
      setAuthToken(session?.accessToken ?? null)
      setState(session ? { status: 'authenticated', session } : { status: 'anonymous', error })
    }

    // Сервер отверг токен — это требование войти заново, а не сбой данных.
    // Без этого экран оставался с общей строкой ошибки и без пути ко входу.
    setUnauthorizedHandler(() => {
      apply(null)
      void signIn()
    })

    restoreSession()
      .then((s) => apply(s))
      .catch((e: unknown) => apply(null, e instanceof Error ? e.message : String(e)))

    // Молчаливое обновление выдаёт новый токен — старый в data/api.ts протух бы.
    const onLoaded = (user: Parameters<Parameters<typeof userManager.events.addUserLoaded>[0]>[0]) =>
      apply(toSession(user))
    const onGone = () => apply(null)

    userManager.events.addUserLoaded(onLoaded)
    userManager.events.addUserUnloaded(onGone)
    userManager.events.addAccessTokenExpired(onGone)
    userManager.events.addSilentRenewError(onGone)

    return () => {
      cancelled = true
      setUnauthorizedHandler(null)
      userManager.events.removeUserLoaded(onLoaded)
      userManager.events.removeUserUnloaded(onGone)
      userManager.events.removeAccessTokenExpired(onGone)
      userManager.events.removeSilentRenewError(onGone)
    }
  }, [])

  return state
}
